import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { Eraser, Loader2, Check, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface PendingOrderInfo {
  has_pending_order: boolean
  driver_name?: string
  order_number?: string
  pickup_datetime?: string
}

export default function DriverSignPage() {
  const { token } = useParams<{ token: string }>()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [info, setInfo] = useState<PendingOrderInfo | null>(null)
  const [docUrl, setDocUrl] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [signed, setSigned] = useState(false)

  const loadPendingOrder = useCallback(async () => {
    if (!token) return
    setIsLoading(true)
    setError('')
    try {
      const res = await apiClient.get<PendingOrderInfo>(`/public/driver-sign/${token}`)
      setInfo(res.data)
      if (res.data.has_pending_order) {
        const docRes = await apiClient.get(`/public/driver-sign/${token}/document`, { responseType: 'blob' })
        setDocUrl(URL.createObjectURL(docRes.data))
      } else {
        setDocUrl(null)
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || '連結無效或已失效')
    } finally {
      setIsLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadPendingOrder()
  }, [loadPendingOrder])

  useEffect(() => {
    if (info?.has_pending_order && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d')
      if (ctx) {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height)
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 2
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
      }
      setHasSignature(false)
    }
  }, [info])

  const getPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if ('touches' in e) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY }
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    setIsDrawing(true)
    const { x, y } = getPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const { x, y } = getPos(e, canvas)
    ctx.lineTo(x, y)
    ctx.stroke()
    setHasSignature(true)
  }

  const stopDrawing = () => setIsDrawing(false)

  const clearSignature = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setHasSignature(false)
  }

  const submitSignature = async () => {
    if (!canvasRef.current || !hasSignature || !token) return
    setIsSaving(true)
    setError('')
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png')
      const blob = await (await fetch(dataUrl)).blob()
      const formData = new FormData()
      formData.append('file', blob, 'signature.png')
      await apiClient.post(`/public/driver-sign/${token}/sign`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setSigned(true)
    } catch (e: any) {
      setError(e?.response?.data?.detail || '簽名送出失敗，請稍後再試')
    } finally {
      setIsSaving(false)
    }
  }

  const signAnother = () => {
    setSigned(false)
    loadPendingOrder()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center space-y-2">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
          <p className="text-gray-700 dark:text-gray-300">{error}</p>
        </div>
      </div>
    )
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center space-y-3">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">簽名已送出，謝謝！</p>
          <Button variant="outline" onClick={signAnother}>查看下一筆</Button>
        </div>
      </div>
    )
  }

  if (!info?.has_pending_order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center space-y-2">
          <p className="text-gray-500 dark:text-gray-400">{info?.driver_name ? `${info.driver_name}，` : ''}目前沒有待簽名的訂單</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-3 flex flex-col gap-3 max-w-lg mx-auto">
      <div className="text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">{info.driver_name} - {info.order_number}</p>
      </div>

      {docUrl && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white">
          <img src={docUrl} alt="派車單" className="w-full object-contain" />
        </div>
      )}

      <p className="text-sm text-gray-600 dark:text-gray-400">請在下方簽名：</p>
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={500}
          height={250}
          className="w-full touch-none cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="flex justify-between gap-3 pb-4">
        <Button variant="outline" onClick={clearSignature}>
          <Eraser className="w-4 h-4 mr-2" />
          清除
        </Button>
        <Button onClick={submitSignature} disabled={!hasSignature || isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              送出中...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              送出簽名
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
